import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TabellinoModal from "@/components/TabellinoModal";
import TavolinoBadge from "@/components/TavolinoBadge";

/**
 * LeagueCalendar — view + entry of league matches grouped by giornata.
 *
 * Props:
 *  - divisionCode: e.g. "TOP"
 *  - accent: division accent color
 */
export default function LeagueCalendar({ divisionCode, accent }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [calendar, setCalendar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [openMatchId, setOpenMatchId] = useState(null);
    const [selectedGiornata, setSelectedGiornata] = useState(1);

    const load = async () => {
        try {
            const { data } = await api.get(`/leagues/${divisionCode}/calendar`);
            setCalendar(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [divisionCode]);

    const generate = async (force = false) => {
        if (force && !confirm("Rigenerare il calendario? Tutti i tabellini esistenti verranno cancellati.")) return;
        setGenerating(true);
        setError(null);
        try {
            await api.post(`/leagues/${divisionCode}/generate-calendar`, null, { params: force ? { force: true } : {} });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setGenerating(false); }
    };

    const remove = async () => {
        if (!confirm("Cancellare il calendario di questa divisione?\nTutte le partite e i tabellini verranno eliminati. La divisione resterà senza calendario.")) return;
        setGenerating(true);
        setError(null);
        try {
            await api.delete(`/leagues/${divisionCode}/calendar`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setGenerating(false); }
    };

    if (loading) {
        return <div className="op-label text-t8f95a3 animate-pulse mt-6">// CARICAMENTO CALENDARIO…</div>;
    }

    const total = calendar?.total_matches || 0;
    const giornate = calendar?.giornate || [];

    if (total === 0) {
        return (
            <div className="op-card p-10 mt-6 text-center" data-testid="calendar-empty">
                <div className="text-5xl mb-4">📅</div>
                <div className="text-tffffff op-heading text-2xl mb-2">Calendario non ancora generato</div>
                <p className="text-t8f95a3 text-sm max-w-md mx-auto mb-6">
                    {isAdmin
                        ? "Genera il calendario stagionale con un click: andata + ritorno, round-robin automatico."
                        : "L'admin deve ancora generare il calendario stagionale."}
                </p>
                {isAdmin && (
                    <button
                        onClick={() => generate(false)}
                        disabled={generating}
                        className="op-btn op-btn-primary"
                        data-testid="generate-calendar-btn"
                    >
                        {generating ? "Generazione…" : "Genera calendario"}
                    </button>
                )}
                {error && <div className="text-tff4d4d text-sm mt-4">{error}</div>}
            </div>
        );
    }

    const currentGiornata = giornate.find((g) => g.giornata === selectedGiornata) || giornate[0];

    return (
        <div className="mt-6" data-testid="calendar-view">
            {/* Giornata selector */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="op-label text-[10px] text-tff7a1a">GIORNATA</div>
                <div className="flex flex-wrap gap-1">
                    {giornate.map((g) => {
                        const playedCount = g.matches.filter((m) => m.status === "played").length;
                        const allPlayed = playedCount === g.matches.length;
                        return (
                            <button
                                key={g.giornata}
                                onClick={() => setSelectedGiornata(g.giornata)}
                                className={`op-label text-[10px] px-2.5 py-1 border transition-colors ${
                                    selectedGiornata === g.giornata
                                        ? "border-t2bb6ff text-tffffff bg-t2bb6ff/15"
                                        : allPlayed
                                            ? "border-t7ce38b/40 text-t7ce38b hover:bg-t7ce38b/10"
                                            : "border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60"
                                }`}
                                data-testid={`giornata-btn-${g.giornata}`}
                            >
                                {g.giornata}
                            </button>
                        );
                    })}
                </div>
                {isAdmin && (
                    <div className="ml-auto flex gap-2">
                        <button
                            onClick={() => generate(true)}
                            disabled={generating}
                            className="op-btn op-btn-ghost text-xs text-tff7a1a"
                            data-testid="regenerate-calendar-btn"
                        >
                            {generating ? "…" : "Rigenera calendario"}
                        </button>
                        <button
                            onClick={remove}
                            disabled={generating}
                            className="op-btn op-btn-ghost text-xs text-tff4d4d"
                            data-testid="delete-calendar-btn"
                        >
                            {generating ? "…" : "Cancella calendario"}
                        </button>
                    </div>
                )}
            </div>

            {/* Matches list */}
            {currentGiornata && (
                <div className="space-y-2" data-testid={`giornata-${currentGiornata.giornata}-matches`}>
                    <div className="op-label text-[11px] text-t8f95a3">
                        // GIORNATA {currentGiornata.giornata} · {currentGiornata.matches.length} partite
                    </div>
                    {currentGiornata.matches.map((m) => (
                        <MatchCard
                            key={m.id}
                            match={m}
                            user={user}
                            accent={accent}
                            onOpen={() => setOpenMatchId(m.id)}
                        />
                    ))}
                </div>
            )}

            {error && <div className="text-tff4d4d text-sm mt-4">{error}</div>}

            {openMatchId && (
                <TabellinoModal
                    matchId={openMatchId}
                    onClose={() => setOpenMatchId(null)}
                    onSaved={() => { load(); }}
                />
            )}
        </div>
    );
}

function MatchCard({ match, user, accent, onOpen }) {
    const played = match.status === "played";
    const tid = String(user?.team_id || "");
    const isMyMatch = tid && (tid === match.home_team_id || tid === match.away_team_id);
    const canEdit = user?.role === "admin" || isMyMatch;

    return (
        <div
            className={`op-card p-3 flex items-center gap-3 transition-colors ${
                canEdit ? "cursor-pointer hover:border-t2bb6ff" : "cursor-pointer hover:border-t22252a"
            } ${isMyMatch ? "border-l-4" : ""}`}
            style={isMyMatch ? { borderLeftColor: "rgb(var(--tc-e8ff00))" } : {}}
            onClick={onOpen}
            data-testid={`match-card-${match.id}`}
        >
            <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                <span className="text-tffffff text-sm truncate text-right">{match.home_team?.name}</span>
                {match.home_team?.logo_url && (
                    <img src={match.home_team.logo_url} alt="" className="w-7 h-7 object-contain flex-shrink-0" />
                )}
            </div>
            <div className="flex flex-col items-center gap-1 px-3 min-w-[110px] justify-center">
                {played ? (
                    <div className="op-mono text-lg text-tffffff font-bold">
                        {match.home_score} <span className="text-t5c6370">-</span> {match.away_score}
                    </div>
                ) : (
                    <div className="op-label text-[10px] text-t5c6370">VS</div>
                )}
                {match.date_from && (
                    <div className="op-mono text-[9px] text-t5c6370 leading-none" title={`Finestra: ${match.date_from} → ${match.date_to} · termine ${match.deadline}`}>
                        {fmtDM(match.date_from)}–{fmtDM(match.date_to)}
                    </div>
                )}
                {match.auto_result && (
                    <TavolinoBadge idSuffix={`m-${match.id}`} definitive={match.is_definitive} className="sm:hidden mt-0.5" />
                )}
            </div>
            <div className="flex-1 flex items-center gap-2 min-w-0">
                {match.away_team?.logo_url && (
                    <img src={match.away_team.logo_url} alt="" className="w-7 h-7 object-contain flex-shrink-0" />
                )}
                <span className="text-tffffff text-sm truncate">{match.away_team?.name}</span>
            </div>
            <div className="hidden sm:block">
                {match.auto_result ? (
                    <TavolinoBadge idSuffix={match.id} definitive={match.is_definitive} />
                ) : played ? (
                    <span className="op-label text-[9px] px-2 py-0.5 border border-t7ce38b/40 text-t7ce38b">
                        GIOCATA
                    </span>
                ) : match.window_open ? (
                    <span className="op-label text-[9px] px-2 py-0.5 border border-te8ff00/50 text-te8ff00">
                        APERTA
                    </span>
                ) : match.date_from ? (
                    <span className="op-label text-[9px] px-2 py-0.5 border border-t22252a text-t5c6370" title={`Il tabellino si attiva il ${match.date_from}`}>
                        dal {fmtDM(match.date_from)}
                    </span>
                ) : (
                    <span className="op-label text-[9px] px-2 py-0.5 border border-t22252a text-t5c6370">
                        DA GIOCARE
                    </span>
                )}
            </div>
        </div>
    );
}

function fmtDM(iso) {
    if (!iso || iso.length < 10) return iso || "";
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}
